import { useState } from 'react';
import { useCreateProject, type Deliverable } from './CreateProjectContext';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import DetailsDeliverableCard from './DetailsDeliverableCard';
import supabase from '@/utils/supabase';
import MarkdownDisplay from '../../../../components/MarkdownDisplay';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Review() {
  const { formState, setFormState, resetForm } = useCreateProject();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  function handleCancel() {
    resetForm();
    navigate('/client');
  }

  function onUpdate(index: number, updated: Partial<Deliverable>) {
    setFormState((prev) => {
      const updatedDeliverables = [...prev.deliverables];
      updatedDeliverables[index] = {
        ...updatedDeliverables[index],
        ...updated,
      };
      return {
        ...prev,
        deliverables: updatedDeliverables,
      };
    });
  }

  async function onSubmit() {
    setIsSubmitting(true);
    const projectData = {
      project_name: formState.projectName,
      brief: formState.brief,
      due_date: formState.dueDate,
      deliverables: formState.deliverables.map((deliverable, index) => {
        return {
          deliverable_name: deliverable.deliverableName,
          video_type: deliverable.videoType,
          aspect_ratio: deliverable.aspectRatio,
          resolution: deliverable.resolution,
          length_range: deliverable.lengthRange,
          frame_rate: deliverable.frameRate,
          notes: deliverable.notes,
          sort_order: index,
        };
      }),
    };

    const {
      data: { session },
    } = await supabase.auth.getSession();

    fetch('/api/client/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify(projectData),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        navigate(`../../projects/${data.id}`, {
          state: { initialTab: 'media' },
        });
        setTimeout(() => resetForm(), 0);
      })
      .catch((error) => {
        console.error('There was an error:', error);
        setIsSubmitting(false);
      });
  }

  const allDeliverables = formState.deliverables.map((deliverable, index) => (
    <DetailsDeliverableCard
      key={deliverable.id}
      deliverable={deliverable}
      index={index}
      onUpdate={onUpdate}
    />
  ));

  return (
    <div className="grid gap-12 min-h-full">
      <div className="col-span-2 flex flex-col max-w-300">
        <h1 className="text-2xl font-bold mb-6">Project Review</h1>
        <div className="flex-1 overflow-y-auto">
          <Card className={`self-start mb-6 w-full`}>
            <CardTitle className="ml-5">
              {formState.projectName || '-'}
            </CardTitle>
            <CardContent className="text-sm grid grid-cols-3 gap-2">
              <div>
                <div className="font-medium">Deliver by</div>
                <div className="text-muted-foreground">
                  {formatDate(formState.dueDate)}
                </div>
              </div>
              <div>
                <div className="font-medium">Videos</div>
                <div className="text-muted-foreground">
                  {formState.deliverables.length}
                </div>
              </div>
              <div className="col-span-3 mt-1">
                <div className="font-medium">Brief</div>
                <MarkdownDisplay
                  content={formState.brief}
                  size={'sm'}
                  variant="simple"
                  expandable={true}
                  className="text-muted-foreground prose-headings:text-muted-foreground prose-strong:text-muted-foreground prose-a:text-muted-foreground prose-blockquote:text-muted-foreground"
                />
              </div>
              {allDeliverables.length > 0 && (
                <div className="col-span-3 mt-3">
                  <div className="font-medium mb-3">Videos</div>
                  <div className="space-y-4">{allDeliverables}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="py-3 flex justify-between gap-2 w-full">
          <Button
            variant={'ghost'}
            className="cursor-pointer"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <div>
            <Button variant={'outline'} className="cursor-pointer mr-2" asChild>
              <Link to="../deliverables">Back</Link>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="cursor-pointer" disabled={isSubmitting}>
                  {isSubmitting ? 'Confirming...' : 'Confirm Project'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm project?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Next, you'll be taken to your project page to upload footage
                    and assets. Editing begins once your media is uploaded.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="cursor-pointer">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="cursor-pointer"
                    onClick={onSubmit}
                  >
                    Confirm
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
